// Vuetify goTo doesn't correctly identify the first scrollable parent so this can be handy to do so and pass into the
// 'container' property of the GoToOptions object.

import { computed } from "vue";
import type VueRouter from "vue-router";
import errorLoggerService from "@/app/errors/errorLoggerService";
import appStore from "@/store/modules/appStore";

// Lifted from: https://stackoverflow.com/questions/35939886/find-first-scrollable-parent#42543908
export function getScrollParent(
  element: Element,
  includeHidden: boolean
): HTMLElement {
  let style = getComputedStyle(element);
  let excludeStaticParent = style.position === "absolute";
  let overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/;

  if (style.position === "fixed") {
    return document.scrollingElement as HTMLElement;
  }

  for (
    let parent: Element | null = element;
    (parent = parent.parentElement);

  ) {
    style = getComputedStyle(parent);
    if (excludeStaticParent && style.position === "static") {
      continue;
    }
    if (
      overflowRegex.test(
        (style.overflow || "") +
          (style.overflowY || "") +
          (style.overflowX || "")
      )
    ) {
      return parent as HTMLElement;
    }
  }

  return document.scrollingElement as HTMLElement;
}

export function isRunningEndToEndTests(): boolean {
  return !!(window as any).Cypress;
}

export const isMacOs = computed(
  () => navigator.userAgent.toLowerCase().indexOf("mac os x") > -1
);

export function detectBrowser() {
  if (
    (navigator.userAgent.indexOf("Opera") ||
      navigator.userAgent.indexOf("OPR")) != -1
  ) {
    return "Opera";
  } else if (navigator.userAgent.indexOf("Chrome") != -1) {
    return "Chrome";
  } else if (navigator.userAgent.indexOf("Safari") != -1) {
    return "Safari";
  } else if (navigator.userAgent.indexOf("Firefox") != -1) {
    return "Firefox";
  } else if (navigator.userAgent.indexOf("MSIE") != -1) {
    return "IE";
  } else {
    return "Unknown";
  }
}

/**
 * Opening a window after an async call causes browsers to lose trust in the action, causing pop-up blockers to activate
 * Here we open a window before the async call and update it once the async resources have been fetched/created
 *
 * @param fn Fetches/Creates async dependancies and injects name/params/querys into a router.resolve
 */
export async function windowOpenAsync(
  fn: () => Promise<ReturnType<VueRouter["resolve"]>>
): Promise<void> {
  const newWindow = window.open("", "_blank");

  // If our fetch fails, close the new window
  try {
    const resolvedRoute = await fn();
    newWindow?.open(resolvedRoute.href, "_self");
  } catch (e: any) {
    newWindow?.close();
    errorLoggerService.logError("Error opening window", e);
    appStore.showError(e);
  }
}
